<template>
  <BaseCard
    v-bind="$attrs"
    :url-link="housingLocalityUrl(housingLocality)"
    :url-image="image"
    :badge="badge"
    :hover-text="translate('Kohdekortti: Hover')"
    :means-test="meansTest"
    :income-limit="incomeLimit"
    :own-plot="ownPlot"
    gray
    class="housing-locality-card"
  >
    <template v-slot:default="{title, line}">

      <h3 class="apartment-card__header h5 mb-2">

        <template v-if="useMultilineTitle">
          <div class="base-card__line">
            {{mpcNameParts.mpcNamePart1}}
          </div>
          <div class="base-card__title">
            {{mpcNameParts.mpcNamePart2}}
          </div>
        </template>

        <template v-else>
          <div class="base-card__title">
            {{housingLocality.mpc_name}}
          </div>
        </template>
      </h3>

      <div
        v-if="housingLocality.township && housingLocality.city"
        :class="line"
      >
        {{getAddress}}
      </div>

    </template>

    <template #apartmentSpecs>
      <div class="base-card__specs base-card__specs--hl">
        <div class="base-card__specs-item">
          <span class="base-card__specs-item-inner">
            <HouseIcon /> {{ housingLocality.mpc_talotyyppi ? translate(housingLocality.mpc_talotyyppi) : ''}}
          </span>

          <span class="base-card__specs-item-inner"
            v-if="housingLocality.mpc_asuinhuoneistojenlkm">
            , {{ housingLocality.mpc_asuinhuoneistojenlkm }} {{ translate('asuntoa') }}
          </span>
        </div>

        <div class="base-card__specs-item color-green"
          v-if="mpcValmistuspaiva">
          {{mpcValmistuspaiva}}
        </div>
      </div>
    </template>

    <template #afterLink>

        <a class="link-with-icon link-with-icon--borderless link-with-icon--centered mt-a"
          :href="housingLocalityUrl(housingLocality)">
          {{ctaText}}
          <ChevronIcon />
        </a>
    </template>
  </BaseCard>
</template>

<script>
  import utils from '../mixins/utils';
  import BaseCard from './BaseCard';

  // Icons.
  import HouseIcon from '../icons/HouseIcon.vue';
  import ChevronIcon from '../icons/ChevronIcon.vue';

  // Utils.
  import { formatDate } from '../utils/dateUtils';

  export default {
    components: {
      BaseCard,
      HouseIcon,
      ChevronIcon
    },
    mixins: [
      utils.translate,
      utils.housingLocalityUrl,
    ],
    props: {
      housingLocality: {
        type: Object,
        required: true,
      },
    },
    computed: {
      image() {

        if(this.housingLocality.imagesForWeb.length <= 0 ) {
          return null;
        }
        for(let i = 0; i < this.housingLocality.imagesForWeb.length; i++) {
          if(this.housingLocality.imagesForWeb[i].type !== 'Myyntiasiakirja') {
            const mainPicture = this.housingLocality.imagesForWeb[i];
            return mainPicture ? mainPicture.storage_thumbnail : null;
          }
        }
      },
      badge() {
        const type = this.housingLocality.mpc_asumismuoto;
        const map = {
          Vuokrakohde: {
            text: this.translate('Vuokra (asuntotyyppi)'),
            color: 'green',
          },
          Asumisoikeus: {
            text: this.translate('ASO'),
            color: 'green',
          },
        };
        return map.hasOwnProperty(type) ? map[type] : {
          text: this.housingLocality.mpc_asumismuoto,
          color: 'default',
        };
      },
      ctaText() {
        return this.housingLocality.ava_kohdemyynnissa
          ? this.translate('Kohdekortti: Teksti')
          : this.translate('Kohdekortti: Teksti, uudiskohde')
      },
      incomeLimit() {
        return this.housingLocality.new_tulorajat;
      },
      meansTest() {
        return this.housingLocality.ava_tarveharkinta;
      },
      ownPlot() {
        return this.housingLocality.ava_tontiomistus === "Oma" ? true : false;
      },
      getAddress() {

        return this.housingLocality.mpc_katuosoite + ', ' +
          this.housingLocality.mpc_postinumero + ' ' +
          this.housingLocality.township.name + ' - ' +
          this.housingLocality.city.mpc_name;
      },
      mpcValmistuspaiva() {

        if ( this.housingLocality.mpc_uudiskohde !== true ) {
          return '';
        }

        return this.translate('Arvioitu valmistuminen') + ' ' + formatDate( this.housingLocality.mpc_valmistumispaiva );
      },
      mpcNameParts() {

        const splittedName = this.housingLocality.mpc_name.split(' ');

        return {
          mpcNamePart1: splittedName[0],
          mpcNamePart2: splittedName.slice(1).join(' '),
        }
      },
      /**
       * We don't want to use multiline title only for certain "asumismuotos".
       */
      useMultilineTitle() {

        const multilineTitleAsumismuotos = [
          'Asumisoikeus',
          'Vuokrakohde',
        ];

        return multilineTitleAsumismuotos.includes( this.housingLocality.mpc_asumismuoto );
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "~styles/definitions";

  .housing-locality-card {
    &__title {
      word-break: auto-phrase;
    }
  }

  .housing-locality-card__footer {
    @include font-label-16;
    line-height: r(18);
  }

  .base-card__specs--hl {
    display: flex;
    flex-flow: column;

    margin-bottom: 0;
    margin-top: auto;

    * {
      display:flex;
      flex-flow: row;
    }
  }
</style>
